import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Sparkles, ChevronRight } from 'lucide-react';

// Minimal translations
const translations = {
  nb: {
    login: 'Logg inn',
    startFree: 'Kom i gang',
    badge: 'AI-drevet beslutningssystem for advokater',
    heroLine1: 'Fra kompleksitet',
    heroLine2: 'til klarhet.',
    heroSubtitle: 'Lexy analyserer, prioriterer og forbereder – slik at du kan fokusere på det som faktisk betyr noe.',
    cta: 'Start nå',
    ctaSecondary: 'Se hvordan det fungerer',
    
    // Outcomes section
    outcomesTitle: 'Hva Lexy gir deg',
    outcomes: [
      { title: '20 timer tilbake', desc: 'Hver uke. Automatisert research, dokumentproduksjon og oppfølging.' },
      { title: 'Null tapte frister', desc: 'AI som holder oversikten når du ikke kan.' },
      { title: '40% høyere margin', desc: 'Fast pris til klient. AI gjør 90% av arbeidet.' },
      { title: 'Bedre beslutninger', desc: 'Full saksoversikt. Relevante presedensar. Risikoanalyse.' }
    ],

    // How it works - ultra minimal
    howTitle: 'Slik fungerer det',
    howSteps: [
      { num: '01', title: 'E-post kommer inn', desc: 'Lexy leser, kategoriserer, foreslår neste steg.' },
      { num: '02', title: 'Sak opprettes', desc: 'Automatisk utfylt med all relevant info og dokumenter.' },
      { num: '03', title: 'Du tar kontrollen', desc: 'Fokuser på strategi. AI håndterer resten.' }
    ],

    // Trust
    trustTitle: 'Bygget for norsk juss',
    trustItems: ['GDPR-compliant', 'Norske servere', 'Bank-grade sikkerhet', 'Advokatgodkjent'],

    // Final CTA
    finalTitle: 'Klar til å jobbe smartere?',
    finalSubtitle: 'Ingen kredittkort. Fullverdig sandbox. 5 min setup.',
    finalCta: 'Kom i gang gratis',

    footer: '© 2025 Lexy'
  },
  en: {
    login: 'Log in',
    startFree: 'Get started',
    badge: 'AI-powered decision system for lawyers',
    heroLine1: 'From complexity',
    heroLine2: 'to clarity.',
    heroSubtitle: 'Lexy analyzes, prioritizes and prepares – so you can focus on what actually matters.',
    cta: 'Start now',
    ctaSecondary: 'See how it works',
    
    outcomes: [
      { title: '20 hours back', desc: 'Every week. Automated research, document production and follow-up.' },
      { title: 'Zero missed deadlines', desc: 'AI that keeps track when you can\'t.' },
      { title: '40% higher margin', desc: 'Fixed price to client. AI does 90% of the work.' },
      { title: 'Better decisions', desc: 'Full case overview. Relevant precedents. Risk analysis.' }
    ],
    outcomesTitle: 'What Lexy gives you',

    howTitle: 'How it works',
    howSteps: [
      { num: '01', title: 'Email arrives', desc: 'Lexy reads, categorizes, suggests next step.' },
      { num: '02', title: 'Case created', desc: 'Automatically filled with all relevant info and documents.' },
      { num: '03', title: 'You take control', desc: 'Focus on strategy. AI handles the rest.' }
    ],

    trustTitle: 'Built for Norwegian law',
    trustItems: ['GDPR-compliant', 'Norwegian servers', 'Bank-grade security', 'Lawyer-approved'],

    finalTitle: 'Ready to work smarter?',
    finalSubtitle: 'No credit card. Full sandbox. 5 min setup.',
    finalCta: 'Get started free',

    footer: '© 2025 Lexy'
  }
};

// Language toggle - just a flag
const LanguageToggle = ({ lang, setLang }: { lang: 'nb' | 'en', setLang: (l: 'nb' | 'en') => void }) => (
  <button
    onClick={() => setLang(lang === 'nb' ? 'en' : 'nb')}
    className="text-xl hover:scale-110 transition-transform"
    title={lang === 'nb' ? 'Switch to English' : 'Bytt til norsk'}
  >
    {lang === 'nb' ? '🇳🇴' : '🇬🇧'}
  </button>
);

const LexyLandingDark = () => {
  const navigate = useNavigate();
  const [lang, setLang] = useState<'nb' | 'en'>(() => {
    const saved = localStorage.getItem('lexy_landing_lang');
    return (saved as 'nb' | 'en') || 'nb';
  });

  const t = translations[lang];
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);

  useEffect(() => {
    localStorage.setItem('lexy_landing_lang', lang);
  }, [lang]);

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-[#E8E8E6] selection:bg-purple-500/30 selection:text-white">
      
      {/* ══════════════════════════════════════════════════════════════════════
          HEADER - Linear style, aligned with hero content
      ══════════════════════════════════════════════════════════════════════ */}
      <header className="fixed top-0 w-full z-50 bg-[#0B0B0D]/80 backdrop-blur-xl">
        <div className="w-full px-6 lg:px-[320px] h-16 flex items-center justify-between">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center gap-2.5"
          >
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </div>
            <span className="text-lg font-semibold tracking-tight">Lexy</span>
          </motion.div>

          {/* Right side */}
          <div className="flex items-center gap-5">
            <LanguageToggle lang={lang} setLang={setLang} />
            <button
              onClick={() => navigate('/lexy/login')}
              className="hidden sm:block text-sm text-[#A0A0A0] hover:text-white transition-colors"
            >
              {t.login}
            </button>
            <button
              onClick={() => navigate('/lexy/signup')}
              className="bg-white hover:bg-gray-100 text-black text-sm font-medium px-4 py-2 rounded-md transition-all"
            >
              {t.startFree}
            </button>
          </div>
        </div>
      </header>

      {/* ══════════════════════════════════════════════════════════════════════
          HERO - Linear style: Animated components building the screen
      ══════════════════════════════════════════════════════════════════════ */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col overflow-hidden pt-32">
        
        {/* === ATMOSPHERE === */}
        <div 
          className="fixed inset-0 pointer-events-none z-[1] opacity-[0.025]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
        <div 
          className="absolute pointer-events-none"
          style={{
            top: '10%',
            left: '15%',
            width: '900px',
            height: '700px',
            background: 'radial-gradient(ellipse at center, rgba(139, 92, 246, 0.035) 0%, transparent 70%)',
            filter: 'blur(100px)',
          }}
        />
        <div 
          className="absolute inset-0 pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse 90% 70% at 50% 30%, transparent 0%, rgba(11, 11, 13, 0.5) 100%)',
          }}
        />
        
        {/* Text content */}
        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative z-20 pt-8 lg:pt-12 pb-6 px-6 lg:px-[320px]"
        >
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="text-4xl sm:text-5xl lg:text-[3.5rem] font-semibold leading-[1.06] tracking-[-0.03em] text-white mb-5 max-w-[750px]"
          >
            {lang === 'en' 
              ? 'Lexy is a purpose-built AI system for modern legal practice'
              : 'Lexy er et AI-system bygget for moderne advokatpraksis'
            }
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="text-base lg:text-lg text-[#888888] max-w-lg leading-relaxed mb-10"
          >
            {lang === 'en' 
              ? 'Meet the system for modern law. Streamline case management, document analysis, and client communication.'
              : 'Strømlinjeform sakshåndtering, dokumentanalyse og klientkommunikasjon.'
            }
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex items-center gap-6"
          >
            <button
              onClick={() => navigate('/lexy/signup')}
              className="bg-white hover:bg-gray-100 text-black font-medium px-6 py-2.5 rounded-md transition-all text-sm"
            >
              {lang === 'en' ? 'Start building' : 'Kom i gang'}
            </button>
            <button
              onClick={() => {
                const el = document.getElementById('how-it-works');
                el?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="text-[#888888] hover:text-white font-medium text-sm flex items-center gap-2 transition-colors"
            >
              {lang === 'en' ? 'How it works' : 'Se hvordan det fungerer'}
              <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>
        </motion.div>

        {/* ═══════════════════════════════════════════════════════════════════
            HERO UI - ANIMATED COMPONENTS BUILDING THE SCREEN
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="relative z-10 w-full mt-8 flex-1 overflow-hidden">
          {/* === THE STAGE === */}
          <div
            className="relative w-full max-w-[1400px] mx-auto px-4"
            style={{
              perspective: '2000px',
              perspectiveOrigin: '50% 25%',
            }}
          >
            {/* Ground shadow */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 1 }}
              className="absolute pointer-events-none"
              style={{
                left: '5%',
                right: '5%',
                bottom: '0',
                height: '150px',
                background: 'radial-gradient(ellipse 70% 100% at 50% 0%, rgba(0,0,0,0.4) 0%, transparent 70%)',
                filter: 'blur(40px)',
                zIndex: -1,
              }}
            />
            
            {/* === 3D CONTAINER === */}
            <motion.div
              initial={{ rotateX: 30, rotateY: -10, rotateZ: -5, scale: 0.8, y: 100, opacity: 0 }}
              animate={{ rotateX: 14, rotateY: -6, rotateZ: -3, scale: 1, y: 0, opacity: 1 }}
              transition={{ delay: 0.6, duration: 2, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
              style={{
                transformStyle: 'preserve-3d',
                transformOrigin: '25% 15%',
              }}
            >
              {/* === MAIN APP CONTAINER === */}
              <div 
                className="relative flex rounded-2xl overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(20,20,24,0.95) 0%, rgba(15,15,18,0.98) 100%)',
                  boxShadow: `
                    0 60px 120px -30px rgba(0,0,0,0.6),
                    0 30px 60px -20px rgba(0,0,0,0.4),
                    0 1px 0 rgba(255,255,255,0.05) inset,
                    0 -1px 0 rgba(0,0,0,0.5) inset
                  `,
                  border: '1px solid rgba(255,255,255,0.08)',
                  minHeight: '520px',
                }}
              >
                
                {/* === SIDEBAR === */}
                <motion.div
                  initial={{ x: -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.9, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                  className="w-[220px] flex-shrink-0 border-r border-white/[0.06] p-4 flex flex-col"
                  style={{ background: 'rgba(0,0,0,0.3)' }}
                >
                  {/* Logo */}
                  <motion.div 
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.1, duration: 0.5 }}
                    className="flex items-center gap-2.5 mb-8"
                  >
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
                      <Sparkles className="w-4 h-4 text-white" />
                    </div>
                    <span className="text-white font-semibold">Lexy</span>
                  </motion.div>
                  
                  {/* Nav items */}
                  <div className="space-y-1">
                    {[
                      { icon: '📊', label: 'Oversikt', active: true, delay: 1.2 },
                      { icon: '📥', label: 'Innboks', delay: 1.3 },
                      { icon: '✨', label: 'Arbeidsflate', delay: 1.4 },
                      { icon: '🤖', label: 'AI-arbeidsflate', delay: 1.5 },
                    ].map((item, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ x: -30, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: item.delay, duration: 0.5, ease: 'easeOut' }}
                        className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors ${
                          item.active 
                            ? 'bg-white/10 text-white' 
                            : 'text-[#888] hover:bg-white/5'
                        }`}
                      >
                        <span className="text-sm">{item.icon}</span>
                        <span>{item.label}</span>
                      </motion.div>
                    ))}
                  </div>
                  
                  {/* Divider */}
                  <div className="my-4 border-t border-white/[0.06]" />
                  
                  {/* Secondary nav */}
                  <div className="text-[10px] uppercase tracking-wider text-[#555] mb-2 px-3">Saker</div>
                  <div className="space-y-1">
                    {[
                      { icon: '📁', label: 'Saker', delay: 1.6 },
                      { icon: '📅', label: 'Kalender', delay: 1.7 },
                      { icon: '👥', label: 'Klienter', delay: 1.8 },
                    ].map((item, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ x: -30, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: item.delay, duration: 0.5, ease: 'easeOut' }}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-[#888] hover:bg-white/5 transition-colors"
                      >
                        <span className="text-sm">{item.icon}</span>
                        <span>{item.label}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
                
                {/* === MAIN CONTENT === */}
                <div className="flex-1 flex flex-col overflow-hidden">
                  {/* Header row */}
                  <motion.div
                    initial={{ y: -20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.0, duration: 0.6 }}
                    className="flex items-center justify-between px-6 py-4"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-sm font-medium">
                        LK
                      </div>
                      <div>
                        <div className="text-white font-medium">God kveld, Lexy</div>
                        <div className="text-[#666] text-xs">{lang === 'en' ? 'Saturday 14 December' : 'lørdag 14. desember'}</div>
                      </div>
                    </div>
                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 1.3, duration: 0.4, type: 'spring', stiffness: 200 }}
                      className="flex items-center gap-2"
                    >
                      <div className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-xs text-[#888]">
                        🔍 Søk...
                      </div>
                      <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center">
                        <span className="text-white text-lg">+</span>
                      </div>
                    </motion.div>
                  </motion.div>
                  
                  {/* === STATS BAND - Full width dashboard header === */}
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.15, duration: 0.6 }}
                    className="w-full"
                    style={{
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)',
                      borderTop: '1px solid rgba(255,255,255,0.08)',
                      borderBottom: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <div className="grid grid-cols-4 divide-x divide-white/[0.08]">
                      {[
                        { value: '6', label: 'Aktive saker', delay: 1.25 },
                        { value: '5', label: 'Klienter', delay: 1.35 },
                        { value: '0', label: 'Oppgaver', delay: 1.45 },
                        { value: '0 kr', label: 'Ufakturert', delay: 1.55 },
                      ].map((stat, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ y: 15, opacity: 0 }}
                          animate={{ y: 0, opacity: 1 }}
                          transition={{ delay: stat.delay, duration: 0.5 }}
                          className="text-center py-5 px-4"
                        >
                          <div className="text-3xl font-bold text-white mb-1">{stat.value}</div>
                          <div className="text-[10px] text-[#888] uppercase tracking-wider font-medium">{stat.label}</div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                  
                  {/* === WORK AREA === */}
                  <div className="flex-1 p-5 overflow-hidden">
                    {/* Content grid */}
                    <div className="grid grid-cols-2 gap-4 mb-5">
                    {/* AI Brief card - elevated with shadow */}
                    <motion.div
                      initial={{ y: 40, opacity: 0, scale: 0.95 }}
                      animate={{ y: 0, opacity: 1, scale: 1 }}
                      transition={{ delay: 1.5, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                      className="p-4 rounded-xl"
                      style={{
                        background: 'linear-gradient(145deg, rgba(139,92,246,0.12) 0%, rgba(59,130,246,0.06) 100%)',
                        border: '1px solid rgba(139,92,246,0.25)',
                        boxShadow: '0 8px 32px -8px rgba(139,92,246,0.2), 0 4px 16px -4px rgba(0,0,0,0.3)',
                      }}
                    >
                      <div className="flex items-center gap-2 mb-3">
                        <Sparkles className="w-4 h-4 text-purple-400" />
                        <span className="text-xs text-purple-300 font-medium">AI BRIEF</span>
                      </div>
                      <div className="text-white font-medium mb-1">Opprett ny sak</div>
                      <div className="text-[#888] text-sm mb-3">Perfekt tid for å starte et nytt prosjekt</div>
                      <div className="text-purple-400 text-sm font-medium flex items-center gap-1">
                        Se saker <ArrowRight className="w-3 h-3" />
                      </div>
                    </motion.div>
                    
                    {/* Quick stats - elevated card */}
                    <motion.div
                      initial={{ y: 40, opacity: 0, scale: 0.95 }}
                      animate={{ y: 0, opacity: 1, scale: 1 }}
                      transition={{ delay: 1.6, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                      className="p-4 rounded-xl"
                      style={{
                        background: 'linear-gradient(145deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.02) 100%)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        boxShadow: '0 8px 32px -8px rgba(0,0,0,0.4), 0 4px 16px -4px rgba(0,0,0,0.2)',
                      }}
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <div className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Neste frist</div>
                          <div className="text-white text-sm font-medium">Ingen kommende</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Innboks</div>
                          <div className="text-white text-sm font-medium">0 uleste</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#555] uppercase tracking-wider mb-1">Timer i dag</div>
                          <div className="text-white text-sm font-medium">1.4 t</div>
                        </div>
                        <div>
                          <div className="text-[10px] text-[#555] uppercase tracking-wider mb-1">På sporet</div>
                          <div className="text-green-400 text-sm font-medium">6 saker</div>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                  
                  {/* Cases section - elevated */}
                  <motion.div
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 1.8, duration: 0.7 }}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="text-white font-medium">Saker</div>
                      <div className="text-[#666] text-sm">Se alle →</div>
                    </div>
                    <div className="space-y-2">
                      {[
                        { name: 'Shipping Contract Review', client: 'Fjord Shipping AS', status: 'AKTIV', delay: 1.9 },
                        { name: 'Divorce Settlement', client: 'Confidential', status: 'AKTIV', delay: 2.0 },
                      ].map((caseItem, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ x: 30, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ delay: caseItem.delay, duration: 0.5 }}
                          className="flex items-center justify-between p-3 rounded-lg bg-white/[0.02] border border-white/[0.04] hover:bg-white/[0.04] transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-2 h-2 rounded-full bg-purple-500" />
                            <div>
                              <div className="text-white text-sm">{caseItem.name}</div>
                              <div className="text-[#666] text-xs">{caseItem.client}</div>
                            </div>
                          </div>
                          <div className="text-[10px] text-green-400 bg-green-400/10 px-2 py-0.5 rounded">
                            {caseItem.status}
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                  </div>
                </div>
              </div>
              
              {/* === FLOATING AI CHAT BUBBLE === */}
              <motion.div
                initial={{ x: 80, y: 80, opacity: 0, scale: 0.7, rotateY: -15 }}
                animate={{ x: 0, y: 0, opacity: 1, scale: 1, rotateY: 0 }}
                transition={{ delay: 2.2, duration: 1, ease: [0.16, 1, 0.3, 1] }}
                className="absolute -right-8 bottom-24 w-[260px]"
                style={{
                  transform: 'translateZ(80px)',
                }}
              >
                <div 
                  className="p-4 rounded-2xl"
                  style={{
                    background: 'linear-gradient(145deg, rgba(28,28,38,0.98) 0%, rgba(18,18,26,0.98) 100%)',
                    boxShadow: `
                      0 40px 80px -20px rgba(0,0,0,0.6),
                      0 20px 40px -15px rgba(139,92,246,0.15),
                      0 1px 0 rgba(255,255,255,0.1) inset
                    `,
                    border: '1px solid rgba(139,92,246,0.25)',
                  }}
                >
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                      <span className="text-white text-xs">🤖</span>
                    </div>
                    <div>
                      <div className="text-white text-sm font-medium">Lexy AI</div>
                      <div className="text-green-400 text-[10px] flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                        Online
                      </div>
                    </div>
                  </div>
                  <div className="text-[#aaa] text-sm leading-relaxed">
                    {lang === 'en' 
                      ? '"I have analyzed 3 new documents. Would you like a summary?"'
                      : '"Jeg har analysert 3 nye dokumenter. Vil du ha en oppsummering?"'
                    }
                  </div>
                  <div className="mt-3 flex gap-2">
                    <div className="px-3 py-1.5 rounded-lg bg-purple-600 text-white text-xs font-medium">
                      Ja, vis
                    </div>
                    <div className="px-3 py-1.5 rounded-lg bg-white/10 text-[#888] text-xs">
                      Senere
                    </div>
                  </div>
                </div>
              </motion.div>
              
              {/* === FLOATING NOTIFICATION (TOP) === */}
              <motion.div
                initial={{ y: -40, opacity: 0, scale: 0.8 }}
                animate={{ y: 0, opacity: 1, scale: 1 }}
                transition={{ delay: 2.4, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                className="absolute left-[420px] -top-3 w-[185px]"
                style={{
                  transform: 'translateZ(70px)',
                }}
              >
                <div 
                  className="p-3 rounded-xl flex items-center gap-3"
                  style={{
                    background: 'linear-gradient(145deg, rgba(34,197,94,0.12) 0%, rgba(18,25,18,0.98) 100%)',
                    boxShadow: '0 25px 50px -15px rgba(0,0,0,0.5)',
                    border: '1px solid rgba(34,197,94,0.25)',
                  }}
                >
                  <div className="w-8 h-8 rounded-full bg-green-500/20 flex items-center justify-center">
                    <span className="text-green-400">✓</span>
                  </div>
                  <div>
                    <div className="text-white text-sm font-medium">Dokument signert</div>
                    <div className="text-[#888] text-xs">Fjord Shipping AS</div>
                  </div>
                </div>
              </motion.div>
              
              {/* === FLOATING EMAIL (LEFT SIDE) === */}
              <motion.div
                initial={{ x: -60, y: 20, opacity: 0, scale: 0.8 }}
                animate={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                transition={{ delay: 2.7, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="absolute -left-12 top-[180px] w-[220px]"
                style={{
                  transform: 'translateZ(55px)',
                }}
              >
                <div 
                  className="p-3 rounded-xl"
                  style={{
                    background: 'linear-gradient(145deg, rgba(59,130,246,0.12) 0%, rgba(18,20,28,0.98) 100%)',
                    boxShadow: '0 25px 50px -15px rgba(0,0,0,0.5)',
                    border: '1px solid rgba(59,130,246,0.25)',
                  }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center">
                      <span className="text-blue-400 text-xs">📧</span>
                    </div>
                    <div className="text-[10px] text-blue-400 font-medium">NY E-POST</div>
                  </div>
                  <div className="text-white text-sm font-medium mb-0.5">Re: Kontrakt utkast</div>
                  <div className="text-[#666] text-xs truncate">Per Hansen • Fjord AS</div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          OUTCOMES - What you get. Not features. Results.
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-32 px-6">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="mb-20"
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {t.outcomesTitle}
            </h2>
            <div className="w-12 h-0.5 bg-purple-500" />
          </motion.div>

          <div className="grid md:grid-cols-2 gap-x-16 gap-y-16">
            {t.outcomes.map((item, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: idx * 0.1 }}
                className="group"
              >
                <h3 className="text-xl md:text-2xl font-semibold mb-3 group-hover:text-purple-400 transition-colors">
                  {item.title}
                </h3>
                <p className="text-[#707070] leading-relaxed">
                  {item.desc}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          HOW IT WORKS - Stillness, numbered steps
      ══════════════════════════════════════════════════════════════════════ */}
      <section id="how-it-works" className="py-32 px-6 border-t border-[#1A1A1A]">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="mb-20"
          >
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {t.howTitle}
            </h2>
            <div className="w-12 h-0.5 bg-purple-500" />
          </motion.div>

          <div className="space-y-0">
            {t.howSteps.map((step, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ delay: idx * 0.15 }}
                className="group flex gap-8 py-10 border-b border-[#1A1A1A] last:border-0"
              >
                <div className="text-4xl md:text-5xl font-bold text-[#252525] group-hover:text-purple-500/50 transition-colors tabular-nums">
                  {step.num}
                </div>
                <div className="flex-1 pt-2">
                  <h3 className="text-xl md:text-2xl font-semibold mb-2 group-hover:text-purple-400 transition-colors">
                    {step.title}
                  </h3>
                  <p className="text-[#707070] text-lg">
                    {step.desc}
                  </p>
                </div>
                <ChevronRight className="w-6 h-6 text-[#303030] group-hover:text-purple-500 group-hover:translate-x-1 transition-all self-center" />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          TRUST - Simple badges, no boxes
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-24 px-6 border-t border-[#1A1A1A]">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            className="text-center"
          >
            <h3 className="text-lg font-medium text-[#606060] mb-8">
              {t.trustTitle}
            </h3>
            <div className="flex flex-wrap justify-center gap-6 md:gap-12">
              {t.trustItems.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-center gap-2 text-[#808080]"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
                  <span className="text-sm font-medium">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FINAL CTA - Clean, confident
      ══════════════════════════════════════════════════════════════════════ */}
      <section className="py-32 px-6 border-t border-[#1A1A1A]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
          >
            {/* Subtle glow behind title */}
            <div className="relative inline-block mb-6">
              <div className="absolute inset-0 bg-purple-500/10 blur-3xl rounded-full scale-150" />
              <h2 className="relative text-4xl md:text-5xl font-bold tracking-tight">
                {t.finalTitle}
              </h2>
            </div>
            
            <p className="text-[#707070] text-lg mb-10">
              {t.finalSubtitle}
            </p>
            
            <button
              onClick={() => navigate('/lexy/signup')}
              className="group inline-flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-500 text-white font-medium px-10 py-4 rounded-md text-lg transition-all hover:shadow-xl hover:shadow-purple-500/30"
            >
              {t.finalCta}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════════════════════
          FOOTER - Minimal
      ══════════════════════════════════════════════════════════════════════ */}
      <footer className="py-8 px-6 border-t border-[#1A1A1A]">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between text-sm text-[#505050]">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 rounded bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
              <Sparkles className="w-2.5 h-2.5 text-white" />
            </div>
            <span>{t.footer}</span>
          </div>
          <div className="flex gap-6">
            <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
            <a href="/terms" className="hover:text-white transition-colors">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LexyLandingDark;
